import { Flex, Layout, Modal } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { SubHeader } from 'src/components/v2/Header';
import { Sidebar } from 'src/components/v2/Sidebar';

import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { TicketContext, TicketProvider } from 'src/contexts/ticket.context';
import { HrTicketDetailLayout } from 'src/pages/v2/HrTicket/components/Layout';
import './ExternalLayout.scss';

interface Props {
  children?: React.ReactNode;
  hasSidebar?: boolean;
  hasSubHeader?: boolean;
}

const timeout = 30;

export default function ExternalLayout({ children, hasSidebar = true, hasSubHeader = true }: Props) {
  const {
    ticketSelected,
    setTicketSelected,
    setIsTicketDetailCollapsed,
    setQrCode,
    qrCode,
    setOpenModalSalary,
    setSalaryTicketData,
    setUserInfoQr
  } = useContext(AppContext);
  const { ticketStatusId } = useContext(TicketContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [countdown, setCountdown] = useState(timeout);
  const navigate = useNavigate();
  const { id } = useParams();
  useEffect(() => {
    if (ticketStatusId) {
      setTicketSelected(undefined);
    }
  }, [ticketStatusId, setTicketSelected]);

  useEffect(() => {
    if (ticketSelected === undefined) {
      setIsTicketDetailCollapsed(true);
    }
  }, [ticketSelected, setIsTicketDetailCollapsed]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    // Bắt đầu đếm ngược mỗi giây
    const tick = () => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 0;
        }
        return prev - 1;
      });
      timeoutId = setTimeout(tick, 1000);
    };

    // Hàm reset countdown
    const resetCountdown = () => {
      setCountdown(timeout);
      clearTimeout(timeoutId);
      tick();
    };

    // Xử lý thao tác người dùng
    const handleUserInteraction = () => {
      resetCountdown();
      setIsModalVisible(false);
      Modal.destroyAll(); // Đóng modal nếu đang hiện
    };

    tick();

    window.addEventListener('mousemove', handleUserInteraction);
    window.addEventListener('keydown', handleUserInteraction);
    window.addEventListener('click', handleUserInteraction);

    // Dọn dẹp khi unmount
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('mousemove', handleUserInteraction);
      window.removeEventListener('keydown', handleUserInteraction);
      window.removeEventListener('click', handleUserInteraction);
      Modal.destroyAll();
    };
    // eslint-disable-next-line
  }, [setQrCode, setTicketSelected, setOpenModalSalary, setSalaryTicketData, setUserInfoQr]);

  // Quản lý hiện/tắt modal
  useEffect(() => {
    if (countdown === 10 && !isModalVisible) {
      setIsModalVisible(true);
    }
    if (countdown > 10 && isModalVisible) {
      setIsModalVisible(false);
    }
    if (countdown === 0) {
      setIsModalVisible(false);
      setQrCode(undefined);
      setSalaryTicketData(null);
      setOpenModalSalary(false);
      setUserInfoQr(undefined);
      setTicketSelected(undefined);
      navigate(urlPath.qrScanner.location);
    }
  }, [
    countdown,
    isModalVisible,
    setQrCode,
    setSalaryTicketData,
    setOpenModalSalary,
    setUserInfoQr,
    setTicketSelected,
    navigate
  ]);

  useEffect(() => {
    if (!qrCode) {
      navigate(urlPath.qrScanner.location);
    }
  }, [navigate, qrCode]);

  return (
    <div className='external-layout'>
      <Modal
        open={isModalVisible}
        title='Bạn sắp bị đăng xuất'
        footer={false}
        maskClosable={false}
        centered
        zIndex={1010}
      >
        <Flex vertical gap={30} align='left' justify='center'>
          <span>
            Bạn sẽ bị thoát trong <b>{countdown}</b> giây nữa. Bạn có muốn tiếp tục không?
          </span>
          <span>Chỉ cần di chuyển chuột, nhấn phím hoặc click bất kỳ để tiếp tục phiên làm việc.</span>
        </Flex>
      </Modal>
      <TicketProvider>
        {hasSubHeader && <SubHeader hasSubHeader={hasSubHeader} />}
        <Layout hasSider>
          {hasSidebar && <Sidebar />}
          <Layout hasSider className={!hasSidebar ? 'external-container' : undefined}>
            <Content>
              <div className='external-content-wrapper'>{children}</div>
            </Content>
            {id && <HrTicketDetailLayout />}
          </Layout>
        </Layout>
      </TicketProvider>
    </div>
  );
}
